import { Card } from '@mui/material';
import clsx from 'clsx';
import { PointGeometryMap, PointGeometryMapProps } from 'components/LocationForm/PointGeometryMap';
import { LocationFormToInput } from 'components/LocationForm/utils/formToInput';
import { LocationInputToForm } from 'components/LocationForm/utils/inputToForm';
import { Position2D } from 'deck.gl';
import { getBearingAndDistance, moveLocationGeometry } from 'kineticUtils';
import React, { useContext, useMemo } from 'react';
import { Point, Position } from 'types';
import { LocationFormContext } from '../../context';
import { DETAILS } from '../const';
import { useMapStyles } from './styles';

export type LocationFormMapsProps = Omit<
  PointGeometryMapProps,
  'locationFormVars' | 'setValues' | 'setDirty' | 'onPinDrop' | 'geometry'
> & {
  onPinDrop: () => void;
};

export const LocationPointMap: React.FunctionComponent<LocationFormMapsProps> = ({
  onPinDrop,
  isPinActive,
  setPinActive,
}) => {
  const { values, setValues, setDirty } = useContext(LocationFormContext);
  const classes = useMapStyles();

  const handlePinDrop = (newCoordinates: Position) => {
    onPinDrop();
    setDirty(DETAILS, true);
    setValues((prev) => {
      const prevCoordinates = prev?.geometry?.coordinates;
      const newGeometry: Point = { type: 'Point', coordinates: newCoordinates };

      if (!prevCoordinates) {
        return { ...prev, geometry: newGeometry };
      }

      const { bearing, distance } = getBearingAndDistance(
        prevCoordinates as Position2D,
        newCoordinates as Position2D
      );
      const locationInput = LocationFormToInput(prev);
      const movedLocation = moveLocationGeometry({
        location: locationInput,
        bearing,
        distance,
      });
      const newValues = LocationInputToForm(movedLocation);

      return newValues;
    });
  };

  const coordinates = useMemo(() => values.geometry?.coordinates as Position | undefined, [values]);

  return (
    <Card
      className={clsx(classes.fullHeight, {
        [classes.mapPinActive]: isPinActive,
      })}
    >
      <PointGeometryMap
        onPinDrop={handlePinDrop}
        isPinActive={isPinActive}
        setPinActive={setPinActive}
        coordinates={coordinates}
      />
    </Card>
  );
};
